import { OrderApi } from '@/api';
import { ClientOrderDetails, OrderHistorySkeleton } from '@/components';
import { RoutePath } from '@/config';
import { useAuthContext, useLegalMutationsAllowed, usePagination } from '@/hooks';
import { getOrderStatusConfig } from '@/utils';
import format from 'date-fns/format';

import CurrencyRubIcon from '../assets/icons/currency-rub.svg?react';
import EmptyOrdersIcon from '../assets/icons/empty-orders.svg?react';
import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import type { MouseEvent } from 'react';
import { Link } from 'react-router-dom';

export const ClientOrderHistoryPage = () => {
  const { isAuthorized } = useAuthContext();
  const { allowed: legalMutationsAllowed, requireConsent } =
    useLegalMutationsAllowed();
  const [tab, setTab] = useState<'orders' | 'payments'>('orders');
  const [orderId, setOrderId] = useState<number>();

  const handleCreateOrderClick = (event: MouseEvent<HTMLAnchorElement>) => {
    if (!legalMutationsAllowed) {
      event.preventDefault();
      requireConsent();
    }
  };

  const { data: allOrders = [], isLoading: isOrdersLoading } = useQuery({
    queryKey: [OrderApi.KEY, 'getAll', true],
    queryFn: () => OrderApi.getAll(true),
    enabled: isAuthorized && tab === 'orders',
  });

  const sortedOrders = useMemo(
    () =>
      [...allOrders].sort((a, b) => {
        const dateA = new Date(
          (a as { create_date?: Date }).create_date ?? a.status_date ?? 0,
        ).getTime();
        const dateB = new Date(
          (b as { create_date?: Date }).create_date ?? b.status_date ?? 0,
        ).getTime();
        return dateB - dateA;
      }),
    [allOrders],
  );

  const ordersPagination = usePagination(sortedOrders.length, 10);

  const displayedOrders = sortedOrders.slice(0, ordersPagination.end);

  if (orderId) {
    return (
      <ClientOrderDetails
        orderId={orderId}
        onBack={() => setOrderId(undefined)}
      />
    );
  }

  /** Шапка таблицы истории — общая для загруженного состояния и скелетона. */
  const historyTableHeader = (
    <>
      <div className="relative hidden w-full flex-[0_0_auto] items-start gap-[24px] self-stretch px-0 pb-[20px] pt-0 md:flex">
        <div className="relative mt-[-1.00px] flex-1 text-[1rem] font-normal leading-5 tracking-normal text-[#151515] opacity-40">
          Номер заказа
        </div>
        <div className="relative mt-[-1.00px] flex-1 text-[1rem] font-normal leading-5 tracking-normal text-[#151515] opacity-40">
          Дата заказа
        </div>
        <div className="relative mt-[-1.00px] flex-1 text-[1rem] font-normal leading-5 tracking-normal text-[#151515] opacity-40">
          Лекарства
        </div>
        <div className="relative mt-[-1.00px] flex-1 text-[1rem] font-normal leading-5 tracking-normal text-[#151515] opacity-40">
          Цена
        </div>
        <div className="relative mt-[-1.00px] flex-1 text-[1rem] font-normal leading-5 tracking-normal text-[#151515] opacity-40">
          Статус
        </div>
      </div>
      <div className="hidden h-px w-full bg-[#151515]/10 md:block" />
    </>
  );

  return (
    <div className="relative flex min-h-full flex-col items-stretch gap-[24px] pb-[24px] pl-0">
      <div className="relative inline-flex flex-[0_0_auto] items-start gap-4">
        <button
          className={`relative inline-flex flex-[0_0_auto] items-start gap-[24px] rounded-[10px] px-4 py-1 ${
            tab === 'orders' ? 'bg-[#151515]' : 'bg-white'
          }`}
          onClick={() => setTab('orders')}
          type="button"
        >
          <div
            className={`relative mt-[-1.00px] w-fit whitespace-nowrap text-[1rem] font-normal leading-6 tracking-normal ${
              tab === 'orders' ? 'text-white' : 'text-[#151515]'
            }`}
          >
            Заказы
          </div>
        </button>
        <button
          className={`relative inline-flex flex-[0_0_auto] items-start gap-[24px] rounded-[10px] px-4 py-1 ${
            tab === 'payments' ? 'bg-[#151515]' : 'bg-white'
          }`}
          onClick={() => setTab('payments')}
          type="button"
        >
          <div
            className={`relative mt-[-1.00px] w-fit whitespace-nowrap text-[1rem] font-normal leading-6 tracking-normal ${
              tab === 'payments' ? 'text-white' : 'text-[#151515]'
            }`}
          >
            Платежи
          </div>
        </button>
      </div>

      {tab === 'orders' && (
        <div
          className={`relative mb-[-16px] flex w-full flex-col items-stretch overflow-hidden rounded-2xl ${
            sortedOrders.length === 0 ? 'min-h-[60vh] flex-1' : ''
          }`}
        >
          <div className="relative flex w-full flex-[0_0_auto] items-center rounded-t-[16px] bg-[#e4f0f7] px-[24px] py-[16px]">
            <div className="relative -mt-px w-fit whitespace-nowrap font-['Ubuntu'] text-[20px] font-medium leading-[30px] tracking-normal text-[#151515]">
              История заказов
            </div>
          </div>

          <div
            className={`relative flex w-full flex-col items-start gap-[24px] rounded-b-[16px] bg-white p-[24px] ${
              sortedOrders.length === 0 ? 'min-h-0 flex-1' : 'flex-[0_0_auto]'
            }`}
          >
            {isOrdersLoading ? (
              // Пока грузится — реальная шапка таблицы и строки-заглушки.
              <div className="relative flex w-full flex-col items-start self-stretch">
                {historyTableHeader}
                <OrderHistorySkeleton />
              </div>
            ) : sortedOrders.length === 0 ? (
              <div className="flex flex-1 flex-col items-center justify-center gap-[32px] self-stretch rounded-[16px] p-[32px]  outline outline-1 outline-offset-[-1px] outline-[#151515]/10">
                <div className="inline-flex items-center justify-center rounded-[100px] bg-[#e4f0f7] p-[16px]">
                  <EmptyOrdersIcon className="size-[48px] text-[#151515]" />
                </div>
                <div className="flex flex-col items-center gap-[8px] text-center">
                  <span className="font-['Ubuntu'] text-[20px] font-normal leading-[30px] text-[#151515] opacity-50">
                    У вас пока нет заказов в истории.
                  </span>
                  <span className="font-['Ubuntu'] text-[20px] font-normal leading-[30px] text-[#151515] opacity-50">
                    Но вы можете оформить новый по кнопке ниже.
                  </span>
                </div>
                <Link
                  to={RoutePath.ClientCreateOrder}
                  onClick={handleCreateOrderClick}
                  aria-disabled={!legalMutationsAllowed}
                  className={`inline-flex h-[60px] items-center justify-center gap-[10px] rounded-[12px] px-[32px] py-[20px] transition-colors ${
                    legalMutationsAllowed
                      ? 'bg-[#0788f1] hover:bg-[#0670c4]'
                      : 'cursor-not-allowed bg-[#0788f1] opacity-50'
                  }`}
                >
                  <span className="text-center font-['Ubuntu'] text-[16px] font-bold leading-[20px] text-white">
                    Сделать заказ
                  </span>
                </Link>
              </div>
            ) : (
              <>
                <div className="relative flex w-full flex-[0_0_auto] flex-col items-start self-stretch">
                  {historyTableHeader}

                  {displayedOrders.map((order, index) => {
                    const isHighlighted = order.status.value === 'Оформлен';
                    return (
                      <div key={order.id} className="w-full">
                        {/* Desktop row */}
                        <div
                          className={`relative hidden w-full flex-[0_0_auto] items-start gap-6 self-stretch px-0 py-5 md:flex ${
                            isHighlighted ? 'bg-[#f1f7fa]' : ''
                          }`}
                        >
                          <div
                            className="relative mt-[-1.00px] flex-1 cursor-pointer text-[1rem] font-normal leading-5 tracking-normal text-[#151515] underline"
                            onClick={() => setOrderId(order.id)}
                          >
                            №{order.id}
                          </div>
                          <div className="relative mt-[-1.00px] flex-1 text-[1rem] font-normal leading-5 tracking-normal text-[#151515]">
                            {format(
                              (order as { create_date?: Date }).create_date ??
                                order.status_date,
                              'dd.MM.yyyy',
                            )}
                          </div>
                          <div
                            className="relative mt-[-1.00px] flex-1 cursor-pointer text-[1rem] font-normal leading-5 tracking-normal text-[#151515] underline"
                            onClick={() => setOrderId(order.id)}
                          >
                            {order.medicine_count}
                          </div>
                          <div className="relative flex flex-1 grow items-center gap-0.5">
                            <div className="relative mt-[-1.00px] w-fit whitespace-nowrap text-[1rem] font-normal leading-5 tracking-normal text-[#151515]">
                              {(
                                order.total_price ?? order.price
                              ).toLocaleString('ru-RU', {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}
                            </div>
                            <CurrencyRubIcon className="relative size-3.5" />
                          </div>
                          <div className="relative mt-[-1.00px] flex-1">
                            {(() => {
                              const cfg = getOrderStatusConfig(
                                order.status?.value ?? '',
                              );
                              return (
                                <span
                                  className={`inline-flex items-center rounded-[100px] px-[12px] py-[4px] text-[14px] font-medium ${cfg.bg} ${cfg.text}`}
                                >
                                  {cfg.label}
                                </span>
                              );
                            })()}
                          </div>
                        </div>

                        {/* Mobile card — как у доктора: не переносить подписи, стабильная сетка */}
                        <div
                          className={`md:hidden ${
                            isHighlighted ? 'bg-[#f1f7fa]' : ''
                          } rounded-[16px]`}
                        >
                          <div className="flex flex-col gap-[12px]">
                            <div className="flex min-w-0 items-center justify-between gap-[16px]">
                              <span className="shrink-0 whitespace-nowrap text-[1rem] font-normal leading-5 text-[#151515] opacity-40">
                                Номер заказа
                              </span>
                              <button
                                type="button"
                                onClick={() => setOrderId(order.id)}
                                className="min-w-0 truncate text-right text-[1rem] font-normal leading-5 text-[#151515] underline"
                              >
                                №{order.id}
                              </button>
                            </div>
                            <div className="flex min-w-0 items-center justify-between gap-[16px]">
                              <span className="shrink-0 whitespace-nowrap text-[1rem] font-normal leading-5 text-[#151515] opacity-40">
                                Дата заказа
                              </span>
                              <span className="min-w-0 truncate text-right text-[1rem] font-normal leading-5 text-[#151515]">
                                {format(
                                  (order as { create_date?: Date })
                                    .create_date ?? order.status_date,
                                  'dd.MM.yyyy',
                                )}
                              </span>
                            </div>
                            <div className="flex min-w-0 items-center justify-between gap-[16px]">
                              <span className="shrink-0 whitespace-nowrap text-[1rem] font-normal leading-5 text-[#151515] opacity-40">
                                Лекарства
                              </span>
                              <button
                                type="button"
                                onClick={() => setOrderId(order.id)}
                                className="min-w-0 truncate text-right text-[1rem] font-normal leading-5 text-[#151515] underline"
                              >
                                {order.medicine_count}
                              </button>
                            </div>
                            <div className="flex min-w-0 items-center justify-between gap-[16px]">
                              <span className="shrink-0 whitespace-nowrap text-[1rem] font-normal leading-5 text-[#151515] opacity-40">
                                Цена
                              </span>
                              <div className="flex min-w-0 shrink-0 items-center gap-[4px] text-[1rem] font-normal leading-5 text-[#151515]">
                                <span>
                                  {(
                                    order.total_price ?? order.price
                                  ).toLocaleString('ru-RU', {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  })}
                                </span>
                                <CurrencyRubIcon className="size-[14px]" />
                              </div>
                            </div>
                            <div className="flex min-w-0 items-center justify-between gap-[16px]">
                              <span className="shrink-0 whitespace-nowrap text-[1rem] font-normal leading-5 text-[#151515] opacity-40">
                                Статус
                              </span>
                              <span className="min-w-0 truncate text-right">
                                {(() => {
                                  const cfg = getOrderStatusConfig(
                                    order.status?.value ?? '',
                                  );
                                  return (
                                    <span
                                      className={`inline-flex items-center rounded-[100px] px-[12px] py-[4px] text-[14px] font-medium ${cfg.bg} ${cfg.text}`}
                                    >
                                      {cfg.label}
                                    </span>
                                  );
                                })()}
                              </span>
                            </div>
                          </div>
                        </div>
                        {index < displayedOrders.length - 1 && (
                          <svg
                            className="relative my-[16px] h-px w-full md:block"
                            viewBox="0 0 912 1"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            preserveAspectRatio="none"
                          >
                            <line
                              opacity="0.1"
                              x1="-4.37081e-08"
                              y1="0.500038"
                              x2="911.93"
                              y2="0.499958"
                              stroke="#151515"
                              vectorEffect="non-scaling-stroke"
                            />
                          </svg>
                        )}
                        {/* Mobile divider between cards */}
                        {index < displayedOrders.length - 1 && (
                          <div className="h-px w-full bg-[#151515]/10 md:hidden" />
                        )}
                      </div>
                    );
                  })}
                </div>

                {ordersPagination.isActive && (
                  <button
                    className="relative w-[137.13px] font-['Ubuntu'] text-[16px] font-normal leading-[24px] tracking-normal text-[#0788f1] underline"
                    onClick={ordersPagination.forward}
                    type="button"
                  >
                    Смотреть больше
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {tab === 'payments' && (
        <div className="relative mb-[-16px] flex min-h-[60vh] w-full flex-1 flex-col overflow-hidden rounded-2xl">
          <div className="relative flex w-full flex-[0_0_auto] items-center rounded-t-[16px] bg-[#e4f0f7] px-[24px] py-[16px]">
            <div className="relative -mt-px w-fit whitespace-nowrap font-['Ubuntu'] text-[20px] font-medium leading-[30px] tracking-normal text-[#151515]">
              Платежи
            </div>
          </div>
          <div className="relative flex min-h-0 flex-1 flex-col items-stretch rounded-b-[16px] bg-white p-[24px]">
            <div className="flex flex-1 flex-col items-center justify-center gap-[32px] self-stretch rounded-[16px] p-[32px] outline outline-1 outline-offset-[-1px] outline-[#151515]/10">
              <div className="inline-flex items-center justify-center rounded-[100px] bg-[#e4f0f7] p-[16px]">
                <EmptyOrdersIcon className="size-[48px] text-[#151515]" />
              </div>
              <div className="flex flex-col items-center gap-[8px] text-center">
                <span className="font-['Ubuntu'] text-[20px] font-normal leading-[30px] text-[#151515] opacity-50">
                  У вас пока нет платежей.
                </span>
                <span className="font-['Ubuntu'] text-[20px] font-normal leading-[30px] text-[#151515] opacity-50">
                  Здесь будут отображаться оплаты по заказам.
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
